import { ArticleUsageInfoPropertyDef } from '@article/constants/article-usage-info-property-def';
import { ArticleUsageVO } from '@article/model/vo/article.vo';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { DefinedMetaElementInfoPropertyDef } from '@project/constants/defined-meta-element-info-property-def';
import { DefinedMetaElementPropertyUtils } from '@article/util/defined-meta-element-property-utils';
import { PropertyUtils } from '@article/util/property-utils';
import { Injectable } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class ReferenceArticleUsageUtils {
  constructor(private _componentUtils: ContainerService) {}

  public static getForwardReferenceArticleUsageIds(articleUsage: ArticleUsageVO): string[] {
    return ArticleUsageUtils.getArticleUsageInfoPropertyStringValues(articleUsage, ArticleUsageInfoPropertyDef.FORWARD_REFERENCE_ARTICLE_USAGE_IDS);
  }

  public getForwardReferenceArticleContainers(articleUsage: ArticleUsageVO): ArticleContainer[] {
    const forwardReferenceUsageIds: string[] = ReferenceArticleUsageUtils.getForwardReferenceArticleUsageIds(articleUsage);
    return this._componentUtils.findArticleContainersInGlobalByGuids(forwardReferenceUsageIds);
  }

  public static addForwardReferenceArticleUsageId(articleUsage: ArticleUsageVO, referenceUsageId: string): void {
    if (!articleUsage || !referenceUsageId) {
      return;
    }
    const existingForwardUsageIds: string[] = this.getForwardReferenceArticleUsageIds(articleUsage);
    if (existingForwardUsageIds.indexOf(referenceUsageId) === -1) {
      ArticleUsageUtils.setArticleUsageInfoPropertyStringValue(
        articleUsage,
        ArticleUsageInfoPropertyDef.FORWARD_REFERENCE_ARTICLE_USAGE_IDS,
        referenceUsageId,
        false
      );
    }
  }

  public static getBackwardReferenceArticleUsageIds(articleUsage: ArticleUsageVO): string[] {
    return ArticleUsageUtils.getAllDefinedMetaElementInfoPropertyStringValues(
      articleUsage,
      DefinedMetaElementInfoPropertyDef.BACKWARD_REFERENCE_ARTICLE_USAGE_ID
    );
  }

  public static getBackwardReferenceArticleUsageId(articleUsage: ArticleUsageVO, metaElementName: string): string {
    return ArticleUsageUtils.getDefinedMetaElementInfoPropertyStringValue(
      articleUsage,
      metaElementName,
      DefinedMetaElementInfoPropertyDef.BACKWARD_REFERENCE_ARTICLE_USAGE_ID
    );
  }

  public static removeForwardReferenceArticleUsageId(articleUsage: ArticleUsageVO, referenceUsageId: string): void {
    if (!articleUsage || !referenceUsageId) {
      return;
    }
    const existingForwardUsageIds: string[] = this.getForwardReferenceArticleUsageIds(articleUsage);
    if (existingForwardUsageIds.length === 0) {
      return;
    }
    const indexToRemove: number = existingForwardUsageIds.indexOf(referenceUsageId);
    if (indexToRemove >= 0) {
      existingForwardUsageIds.splice(indexToRemove, 1);
      ArticleUsageUtils.setArticleUsageInfoPropertyStringValues(
        articleUsage,
        ArticleUsageInfoPropertyDef.FORWARD_REFERENCE_ARTICLE_USAGE_IDS,
        existingForwardUsageIds
      );
    }
  }

  public static removeBackwardReferenceArticleUsageId(articleUsage: ArticleUsageVO, referenceUsageId: string): void {
    if (!articleUsage || !referenceUsageId) {
      return;
    }
    DefinedMetaElementPropertyUtils.removePropertiesByNameAndStringValue(
      articleUsage.definedMetaElementInfoProperties,
      DefinedMetaElementInfoPropertyDef.BACKWARD_REFERENCE_ARTICLE_USAGE_ID,
      referenceUsageId
    );
  }

  public static removeReferenceArticleUsageId(articleUsage: ArticleUsageVO, referenceUsageId: string): void {
    this.removeForwardReferenceArticleUsageId(articleUsage, referenceUsageId);
    this.removeBackwardReferenceArticleUsageId(articleUsage, referenceUsageId);
  }

  public static removeAllForwardReferenceArticleUsageIds(articleUsage: ArticleUsageVO): void {
    ArticleUsageUtils.removeArticleUsageInfoProperty(articleUsage, ArticleUsageInfoPropertyDef.FORWARD_REFERENCE_ARTICLE_USAGE_IDS);
  }

  public static removeAllBackwardReferenceArticleUsageIds(articleUsage: ArticleUsageVO): void {
    ArticleUsageUtils.removeDefinedMetaElementInfoPropertiesByName(
      articleUsage,
      DefinedMetaElementInfoPropertyDef.BACKWARD_REFERENCE_ARTICLE_USAGE_ID
    );
  }

  public static removeAllReferences(articleUsage: ArticleUsageVO): void {
    if (articleUsage) {
      this.removeAllForwardReferenceArticleUsageIds(articleUsage);
      this.removeAllBackwardReferenceArticleUsageIds(articleUsage);
    }
  }

  public static replaceReferenceArticleUsageId(articleUsage: ArticleUsageVO, usageIdFrom: string, usageIdTo: string): void {
    if (articleUsage) {
      PropertyUtils.replaceStringPropertyValue(
        articleUsage.infoProperties,
        ArticleUsageInfoPropertyDef.FORWARD_REFERENCE_ARTICLE_USAGE_IDS,
        usageIdFrom,
        usageIdTo
      );
      DefinedMetaElementPropertyUtils.replaceStringPropertyValues(
        articleUsage.definedMetaElementInfoProperties,
        DefinedMetaElementInfoPropertyDef.BACKWARD_REFERENCE_ARTICLE_USAGE_ID,
        usageIdFrom,
        usageIdTo
      );
    }
  }
}
